// The AI assistant's live status, which the mascot follows (AI-001). The voice assistant itself needs
// activation (no AI binding on Workers Free), so today nothing reports "listening" or "speaking": the
// status stays "ready" and the mascot idles. When the assistant ships, its session calls
// setAssistantStatus() at each real event and hands its speech output to setAssistantSpeech():
//
//   mic capture starts         -> "listening"   (mascot: Listening)
//   capture ends, request sent -> "processing"  (Thinking)
//   TTS playback starts        -> "speaking"    (Talking; mouth follows the speech amplitude)
//   reply/task finished        -> "completed"   (Done once, then Idle by itself)
import { useSyncExternalStore } from "react";
import type { MascotState } from "./mascot.types";

export type AssistantStatus = "ready" | "listening" | "processing" | "speaking" | "completed";

export interface AssistantSession {
  status: AssistantStatus;
  /** Analyser on the assistant's speech output, for lip sync; null when nothing is playing. */
  speech: AnalyserNode | null;
}

const listeners = new Set<() => void>();
let session: AssistantSession = { status: "ready", speech: null };

function update(next: Partial<AssistantSession>) {
  session = { ...session, ...next };
  for (const fn of listeners) fn();
}

export function setAssistantStatus(status: AssistantStatus) {
  if (session.status !== status) update({ status });
}

export function setAssistantSpeech(speech: AnalyserNode | null) {
  if (session.speech !== speech) update({ speech });
}

export function useAssistantSession(): AssistantSession {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => session,
  );
}

export function mascotStateFor(status: AssistantStatus): MascotState {
  switch (status) {
    case "listening":
      return "listening";
    case "processing":
      return "thinking";
    case "speaking":
      return "talking";
    case "completed":
      return "done";
    default:
      return "idle";
  }
}

/**
 * Routes an audio element (the TTS reply) through an analyser so the mascot's mouth can follow it;
 * the sound still plays. One AudioContext per element (a media element can be wired only once).
 */
export function analyseSpeech(el: HTMLMediaElement, ctx = new AudioContext()): AnalyserNode {
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  analyser.smoothingTimeConstant = 0.2;
  ctx.createMediaElementSource(el).connect(analyser);
  analyser.connect(ctx.destination);
  return analyser;
}
